import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Bot } from "lucide-react";
import { useTranslation } from "../../hooks/useTranslation";
import { useTenant } from "../../contexts/TenantContext";
import { useBillingAccount, useBillingCatalog } from "../../hooks/useApi";
import { BrandStyles, VARIABILI_SEZIONE } from "../integrations/brand";
import { GRIGIO, SCURO } from "../integrations/casella";
import { GRATUITO, selfService } from "./piani";
import { SchedaPiano } from "./SchedaPiano";

/**
 * «Nuovo assistente» con il limite di assistenti del piano gia' raggiunto:
 * lo si dice subito, prima dell'analisi del sito (che altrimenti si farebbe
 * per niente), e si mostrano i piani che ne comprendono di piu', con le
 * stesse schede della pagina Abbonamento. Chi non gestisce l'abbonamento
 * (collaboratore) o e' stato venduto offline sa a chi chiedere.
 */
export default function LimiteAssistenti({ consumi }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canManageBilling } = useTenant();
  const catalogo = useBillingCatalog();
  const conto = useBillingAccount({ enabled: Boolean(canManageBilling) });

  const piano = consumi?.plan;
  const massimo = consumi?.effective?.limits?.widgets_max || 0;
  const usati = consumi?.usage?.widgets_count || 0;
  const nomePiano = !piano || piano.key === GRATUITO ? t("onboardingWizard.planFree") : piano.name;
  const offline = Boolean(conto.data?.pagamenti?.vendita_offline);
  const pagamentiAttivi = Boolean(conto.data?.pagamenti?.stripe_attivo);

  // I piani che si comprano online e comprendono piu' assistenti (0 = illimitati).
  const superiori = (catalogo.data?.plans || [])
    .filter((p) => p.key !== GRATUITO && selfService(p.key) && (p.price_yearly > 0 || p.price_monthly > 0))
    .filter((p) => !p.limits?.widgets_max || p.limits.widgets_max > massimo)
    .sort((a, b) => (a.price_yearly || a.price_monthly * 12) - (b.price_yearly || b.price_monthly * 12));
  const puoComprare = canManageBilling && !offline && pagamentiAttivi && superiori.length > 0;

  let spiegazione = t("onboardingWizard.botLimitText");
  if (!canManageBilling) spiegazione = t("onboardingWizard.botLimitAskOwner");
  else if (offline) spiegazione = t("onboardingWizard.botLimitOffline");
  else if (!superiori.length) spiegazione = t("onboardingWizard.botLimitNoHigher");

  return (
    <div className="flex flex-col items-center px-4 pb-14 pt-10" style={VARIABILI_SEZIONE}>
      <BrandStyles />
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFFBEB] text-[#B45309]" aria-hidden="true">
        <Bot className="h-6 w-6" />
      </span>
      <h1 className="mt-5 max-w-[640px] text-center text-[28px] font-semibold leading-9 text-[#020617]">
        {t("onboardingWizard.botLimitTitle", { plan: nomePiano })}
      </h1>
      <p className="mt-3 max-w-[560px] text-center text-base leading-7 text-[#64748B]">
        {t("onboardingWizard.botLimitCount", { count: usati, max: massimo })} {spiegazione}
      </p>

      {puoComprare ? (
        // Al centro anche con uno o due piani: la griglia e' larga quanto le schede.
        <div
          className={`mt-10 grid w-full gap-5 ${
            superiori.length === 1
              ? "max-w-[340px]"
              : superiori.length === 2
                ? "max-w-[680px] md:grid-cols-2"
                : "max-w-[1000px] md:grid-cols-3"
          }`}
        >
          {superiori.map((p, i) => (
            <SchedaPiano
              key={p.key}
              piano={p}
              intervallo="yearly"
              inEvidenza={i === 0}
              azione={
                <button
                  type="button"
                  className={`${i === 0 ? SCURO : GRIGIO} w-full`}
                  onClick={() => navigate(`/billing?piano=${p.key}`)}
                >
                  {t("onboardingWizard.planCta", { plan: p.name })}
                  <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              }
            />
          ))}
        </div>
      ) : null}

      <button
        type="button"
        className="mt-8 inline-flex h-10 items-center justify-center gap-2 rounded-[10px] bg-[#F1F5F9] px-4 text-sm font-semibold text-[#18181B] transition-colors hover:bg-[#E2E8F0] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => navigate("/bots")}
      >
        {t("onboardingWizard.botLimitBack")}
      </button>
    </div>
  );
}
